import React from 'react';
import { css } from '@emotion/react';
import { useHistory, useParams } from 'react-router-dom';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import api from '../../api';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { IUpdateUrineVolumeReset } from '../../types/api/resident/updateUrineVolumeReset';
import { color } from '../../styles';
import { Dialog } from '../mui/dialog/Dialog';
import { IResident } from '../../types/api/traversal/getNursingHomeResidents';
import { RecordDialog } from './recordDialog';
import { useTranslation } from 'react-i18next';
import { CareRecordFormFrame } from './careRecordFormFrame';
import { CareRecordFormPayload, useCareRecordForm } from '@/hooks/useCareRecordForm';
import { CareRecordUrineSection } from './careRecordUrineSection';
import { CareRecordUrineToiletRowSection } from './careRecordUrineToiletRowSection';
import { CareRecordToiletVolumeSection } from './careRecordToiletVolumeSection';
import { CareRecordPadUrineVolumeTypeSection } from './careRecordPadUrineVolumeTypeSection';
import { CareRecordPadVolumeSection } from './careRecordPadVolumeSection';
import { CareRecordUrineLeakOutCheckSection } from './careRecordUrineLeakOutCheckSection';
import { CareRecordStoolSection } from './careRecordStoolSection';
import { CareRecordUseDiaperSection } from './careRecordUseDiaperSection';
import { CareRecordLaxativeSection } from './careRecordLaxativeSection';
import { CareRecordDrinkSection } from './careRecordDrinkSection';
import { CareRecordWorkSection } from './careRecordWorkSection';

interface Props {
  resident: IResident;
  resetNotification: (residentId: number, coverTypes?: string[]) => void;
  onClose: () => void;
  onUpdate: () => void;
  isDisplayPadUrineVolume?: boolean;
}

const MarginBottom = css`
  margin-bottom: 24px;
`;

const ResultModalContainer = css`
  margin: 0 auto;
`;

const ModalContentContainer = css`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0 24px;
`;

const ModalLargeIcon = css`
  width: 56px;
  min-width: 56px;
  height: 56px;
`;

const ModalText = css`
  font-size: 16px;
  p {
    margin: 0;
  }
`;

const ModalTitleText = css`
  font-size: 18px;
  font-weight: bold;
`;

const ContentGap = css`
  display: flex;
  justify-content: center;
  gap: 16px;
`;

const FlexColumn = css`
  display: flex;
  flex-direction: column;
`;

const UrineMetaChecksContainer = css`
  display: flex;
  align-items: center;
  height: 40px;
`;

const StyledButton = styled(Button)`
  &.MuiButton-outlined {
    background-color: ${color.white};
    color: ${color.dark_gray};
    border: 1px solid ${color.dark_gray};
  }
`;

export const RecordModal = React.memo(({ resident, resetNotification, onClose, onUpdate }: Props) => {
  const { nursingHomeId } = useParams<{ nursingHomeId: string }>();
  const history = useHistory();
  const { showSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation();

  const [isResetModalOpen, setResetModalOpen] = React.useState<boolean>(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = React.useState<boolean>(false);
  const [isAdviceModalOpen, setAdviceModalOpen] = React.useState<boolean>(false);
  const [onSend, setOnSend] = React.useState<boolean>(false);
  const form = useCareRecordForm(onSend);

  const createCareRecord = async (data: CareRecordFormPayload) => {
    return await api.post('/residents/:residentId/care-records', data);
  };

  const updateUrineVolumeReset = async (residentId: number) => {
    const data: IUpdateUrineVolumeReset = {
      residentId,
      urineVolumeResetFlg: true,
    };
    return await api.put('/residents/:residentId/urine-volume-reset', data);
  };

  const handleSubmit = () => {
    setOnSend(true);
    const sendData = form.buildSendData(resident.id);

    createCareRecord(sendData)
      .then(() => {
        let coverTypes: string[] = ['gasPooped', 'gasPrediction'];

        if (
          sendData.urinatedToilet ||
          (sendData.padUrineVolumeType && sendData.padUrineVolumeType !== 'none') ||
          sendData.isLeakOutUrine
        ) {
          coverTypes = [...coverTypes, 'bigBladder', 'urination'];
          updateUrineVolumeReset(resident.id).catch((err) => {
            console.log(err);
          });
        }
        setTimeout(() => {
          resetNotification(resident.id, coverTypes);
        }, 500);

        if (sendData.urinatedToilet && sendData.padUrineVolumeType == 'none' && !sendData.isLeakOutUrine) {
          setSuccessModalOpen(true);
        } else if (sendData.urinatedToilet !== null && sendData.padUrineVolumeType !== null) {
          setAdviceModalOpen(true);
        } else {
          showSnackbar(t('recordSaved', '記録しました'), 'success');
          onClose();
        }
        onUpdate();
      })
      .catch((err) => {
        console.log(err);
        setOnSend(false);
        showSnackbar(t('recordingFailedPleaseTryAgain', '記録に失敗しました 再度お試しください', 'error'));
      });
  };

  const onResetModalSubmit = () => {
    updateUrineVolumeReset(resident.id)
      .then(() => {
        resetNotification(resident.id, ['gasPooped', 'gasPrediction', 'bigBladder', 'urination']);
        onClose();
        setResetModalOpen(false);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const handleClickLineChangeButton = () => {
    history.push(`/nursinghome/${nursingHomeId}/residents/${resident.id}/edit`);
  };

  const handleClickTroubleButton = () => {
    onClose();
    setAdviceModalOpen(false);
    i18n.language.startsWith('ja')
      ? window.open('https://dfree.biz/img/pdf/professional_solution.pdf', '_blank')
      : window.open('https://www.dfreeus.biz/support', '_blank');
  };

  const onSuccessModalClose = () => {
    onClose();
    setSuccessModalOpen(false);
  };

  return (
    <>
      <CareRecordFormFrame
        form={form}
        onClose={onClose}
        onSubmit={handleSubmit}
        onOpenResetModal={() => setResetModalOpen(true)}
        showModeToggle={true}
      >
        <CareRecordUrineSection>
          <CareRecordUrineToiletRowSection form={form} />
          {form.isDisplayAdvancedForm && form.shouldShowToiletVolume && <CareRecordToiletVolumeSection form={form} />}
          <CareRecordPadUrineVolumeTypeSection form={form} />
          {form.isDisplayAdvancedForm && form.shouldShowPadVolume && <CareRecordPadVolumeSection form={form} />}
          <div css={UrineMetaChecksContainer}>
            <CareRecordUrineLeakOutCheckSection form={form} />
          </div>
        </CareRecordUrineSection>
        <CareRecordStoolSection form={form} />
        {form.isDisplayAdvancedForm && (
          <>
            <CareRecordUseDiaperSection form={form} />
            <CareRecordLaxativeSection form={form} />
            <CareRecordDrinkSection form={form} />
            <CareRecordWorkSection form={form} />
          </>
        )}
      </CareRecordFormFrame>
      {isResetModalOpen && (
        <Dialog
          isOpen={isResetModalOpen}
          title={t('wouldYouLikeToResetTo0WoRecording', '記録をせずに0にしますか?')}
          onClose={() => setResetModalOpen(false)}
          onOkClick={onResetModalSubmit}
        >
          <div css={[MarginBottom, ModalText]}>{resident.name}</div>
        </Dialog>
      )}
      {isSuccessModalOpen && (
        <RecordDialog isOpen={isSuccessModalOpen} dialogWidth={'620px'} onClose={onSuccessModalClose}>
          <div css={ResultModalContainer}>
            <img
              css={[MarginBottom, ModalLargeIcon]}
              src='/images/residents/recordModal/record_success.svg'
              alt='トイレ誘導成功'
            />
            <div css={[MarginBottom, ModalTitleText]}>
              {t('goodJobProvidingATimelyAssistance', 'トイレ誘導成功おめでとうございます 👏')}
            </div>
            <div css={[MarginBottom, ModalText]}>
              <p>
                {t(
                  'pleaseContinueToUseDFreeForToiletAssistance',
                  '引き続き、トイレで排尿できるようDFreeをご活用ください',
                )}
              </p>
            </div>
            <Button variant='contained' disableElevation color='secondary' onClick={onSuccessModalClose}>
              {t('close', '閉じる')}
            </Button>
          </div>
        </RecordDialog>
      )}
      {isAdviceModalOpen && (
        <RecordDialog
          isOpen={isAdviceModalOpen}
          dialogWidth={'620px'}
          onClose={() => {
            onClose();
            setAdviceModalOpen(false);
          }}
        >
          <div>
            <div css={ResultModalContainer}>
              <img
                css={[MarginBottom, ModalLargeIcon]}
                src='/images/residents/adviceModal/icon_advice.svg'
                alt='排尿記録完了'
              />
              <div css={[MarginBottom, ModalTitleText]}>
                {t('pleaseContinueToMaintainUrinationRecord', '排尿記録の入力ありがとうございます 👏')}
              </div>
            </div>
            <div css={[MarginBottom, ModalText]}>
              <p>
                {t(
                  'ifYouAreHavingTroubleWithNotificationsModeSettingsOrWithMeasurementsPleaseCheckFAQ',
                  '通知やモード設定、数値に違和感がある場合などお困りのときは、「困った時の対処法」をご確認ください',
                )}
              </p>
            </div>
            <div css={[FlexColumn, ContentGap]}>
              <div css={ModalContentContainer}>
                <Button variant='outlined' disableElevation onClick={handleClickLineChangeButton}>
                  {t('changeNotificationModeSettings', '通知・モード設定の変更')}
                </Button>
                <Button variant='contained' disableElevation onClick={handleClickTroubleButton}>
                  {t('fAQ', '困った時の対処法')}
                </Button>
              </div>
              <div>
                <StyledButton
                  variant='outlined'
                  disableElevation
                  onClick={() => {
                    onClose();
                    setAdviceModalOpen(false);
                  }}
                >
                  {t('close', '閉じる')}
                </StyledButton>
              </div>
            </div>
          </div>
        </RecordDialog>
      )}
    </>
  );
});
